import {
  Autocomplete,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  FormControl,
  FormLabel,
  Grid,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Paper,
  TextField
} from '@mui/material';

import { Field, useFormikContext } from 'formik';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { useEntitlementGroupsQuery } from 'api/hooks/accessHandler';
import { useAccessPermissionsQuery } from 'api/hooks/accessHandler/accessPermission';
import { actions as AlertActions } from 'app/containers/Dashboard/Alert/slice';
import { translations } from 'locales/i18n';

// import './RoleGeneral.scss';

function not(a: readonly number[], b: readonly number[]) {
  return a.filter(value => b.indexOf(value) === -1);
}

function intersection(a: readonly number[], b: readonly number[]) {
  return a.filter(value => b.indexOf(value) !== -1);
}

export function RoleGeneral() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const {
    data: entitlementGroupsData,
    isLoading: entitlementGroupsLoading,
    isError: entitlementGroupsError
  } = useEntitlementGroupsQuery();

  const {
    data: accessPermissions,
    isLoading: accessPermissionsLoading,
    isError: accessPermissionsError
  } = useAccessPermissionsQuery();

  const { setFieldValue, setFieldTouched, values, errors, touched } = useFormikContext<any>();

  const [checked, setChecked] = React.useState<readonly number[]>([]);
  const [left, setLeft] = React.useState<readonly number[]>([]);

  const leftChecked = intersection(checked, left);
  const rightChecked = intersection(checked, values.accessPermissions);

  const handleToggle = (value: number) => () => {
    const currentIndex = checked.indexOf(value);
    const newChecked = [...checked];

    if (currentIndex === -1) {
      newChecked.push(value);
    } else {
      newChecked.splice(currentIndex, 1);
    }

    setChecked(newChecked);
  };

  const handleAllRight = () => {
    setFieldValue('accessPermissions', values.accessPermissions.concat(left));
    setLeft([]);
  };

  const handleCheckedRight = () => {
    setFieldValue('accessPermissions', values.accessPermissions.concat(leftChecked));
    setLeft(not(left, leftChecked));
    setChecked(not(checked, leftChecked));
  };

  const handleCheckedLeft = () => {
    setLeft(left.concat(rightChecked));
    setFieldValue('accessPermissions', not(values.accessPermissions, rightChecked));
    setChecked(not(checked, rightChecked));
  };

  const handleAllLeft = () => {
    setLeft(left.concat(values.accessPermissions));
    setFieldValue('accessPermissions', []);
  };

  const customList = (items: readonly number[], permissionsData: any[]) => (
    <Paper sx={{ width: '100%', height: 400, overflow: 'auto' }}>
      <List dense component="div" role="list">
        {items.map((value: number) => {
          const labelId = `transfer-list-item-${value}-label`;
          const permission = permissionsData.find(permission => permission.id === value);

          return (
            <ListItemButton key={value} role="listitem" onClick={handleToggle(value)}>
              <ListItemIcon>
                <Checkbox
                  checked={checked.indexOf(value) !== -1}
                  tabIndex={-1}
                  disableRipple
                  inputProps={{
                    'aria-labelledby': labelId
                  }}
                />
              </ListItemIcon>
              <ListItemText id={labelId} primary={permission.permission} />
            </ListItemButton>
          );
        })}
        <ListItem />
      </List>
    </Paper>
  );

  if (entitlementGroupsError || accessPermissionsError) {
    dispatch(
      AlertActions.error(`${t(translations.pages.managePermissionsSettings.errorLoading())}`)
    );
    navigate('/', { replace: true });
    return;
  }

  if (accessPermissionsLoading || entitlementGroupsLoading) {
    return <CircularProgress />;
  }
  const accessPermissionsData = accessPermissions!;

  const test = left.concat(values.accessPermissions);
  if (test.length !== accessPermissionsData?.data.length) {
    setLeft(
      not(
        accessPermissionsData?.data.map(permissions => permissions.id),
        values.accessPermissions
      )
    );
  }
  return (
    <Grid container spacing={2} justifyContent="center">
      <Grid item xs={6}>
        <FormControl className="role-modal__input" required>
          <Field
            as={TextField}
            name="name"
            fullWidth
            label={t(translations.pages.roles.model.name())}
            error={touched.name && errors.name}
            helperText={touched.name && errors.name}
            required
            size="small"
          />
        </FormControl>
      </Grid>
      <Grid item xs={12}>
        <Grid container justifyContent="center" alignItems="center">
          <Grid className="role-modal__container" item xs={12}>
            <FormLabel className="role-modal__container__label">
              {t(translations.pages.roles.model.entitlementGroups())}
            </FormLabel>
          </Grid>
          <Grid item xs={12}>
            <Autocomplete
              multiple
              size="small"
              loading={entitlementGroupsLoading}
              options={entitlementGroupsData?.data || []}
              value={
                values.entitlementGroups &&
                values.entitlementGroups.map(selectedEntitlement =>
                  entitlementGroupsData?.data.find(ent => {
                    return ent.id === selectedEntitlement;
                  })
                )
              }
              getOptionDisabled={ent => !!ent.role}
              getOptionLabel={ent => ent.name}
              onChange={(ent, value, reason) => {
                setFieldValue(
                  'entitlementGroups',
                  value.map(ent => ent.id)
                );
              }}
              renderInput={params => (
                <TextField
                  {...params}
                  error={touched.entitlementGroups && !!errors.entitlementGroups}
                  helperText={touched.entitlementGroups && <>{errors.entitlementGroups}</>}
                  variant="outlined"
                  label={t(translations.pages.roles.model.entitlementGroups())}
                  margin="normal"
                  fullWidth
                  onBlur={() => {
                    if (!touched.groupEntitlements) {
                      setFieldTouched('groupEntitlements', true);
                    }
                  }}
                  inputProps={{
                    ...params.inputProps,
                    autoComplete: 'new-password'
                  }}
                />
              )}
              renderTags={(value, getPropTags) => {
                return value.map((ent, index) => (
                  <Chip
                    component={Paper}
                    elevation={2}
                    {...getPropTags({ index: index })}
                    key={ent.id}
                    label={ent.name}
                  />
                ));
              }}
            />
          </Grid>
          <Grid className="role-modal__container" item xs={12}>
            <FormLabel className="role-modal__container__label">
              {t(translations.pages.roles.model.permissions())}
            </FormLabel>
          </Grid>
          <Grid item xs={5}>
            <FormLabel component="h1">{t(translations.pages.roles.modal.notIncluded())}</FormLabel>
            {customList(left, accessPermissionsData?.data)}
          </Grid>
          <Grid item xs={2}>
            <Grid container direction="column" alignItems="center">
              <Button
                sx={{ my: 0.5 }}
                variant="outlined"
                size="small"
                onClick={handleAllRight}
                disabled={left.length === 0}
                aria-label="move all right"
              >
                ≫
              </Button>
              <Button
                sx={{ my: 0.5 }}
                variant="outlined"
                size="small"
                onClick={handleCheckedRight}
                disabled={leftChecked.length === 0}
                aria-label="move selected right"
              >
                &gt;
              </Button>
              <Button
                sx={{ my: 0.5 }}
                variant="outlined"
                size="small"
                onClick={handleCheckedLeft}
                disabled={rightChecked.length === 0}
                aria-label="move selected left"
              >
                &lt;
              </Button>
              <Button
                sx={{ my: 0.5 }}
                variant="outlined"
                size="small"
                onClick={handleAllLeft}
                disabled={values.accessPermissions.length === 0}
                aria-label="move all left"
              >
                ≪
              </Button>
            </Grid>
          </Grid>
          <Grid item xs={5}>
            <FormLabel component="h1">{t(translations.pages.roles.modal.included())}</FormLabel>
            {customList(values.accessPermissions, accessPermissionsData.data)}
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
}
